/* eslint-disable no-console */
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import './CreateOrEditChart.scss';
import { FlightButton } from '@flybits/design-system';
import { useHistory, useParams } from 'react-router-dom';
import Preview from './Preview/Preview';
import UnsavedPrompt from 'components/Shared/UnsavedPrompt/UnsavedPrompt';
import ExperienceSelector from './ExperienceSelector/ExperienceSelector';
import ChartConfigurationType from './ChartConfigurationType/ChartConfigurationType';
import RecommendationsSelector from './RecommendationsSelector/RecommendationsSelector';
import TimeFrameSelector from './TimeFrameSelector/TimeFrameSelector';
import ChartTitleInput from './ChartTitleInput/ChartTitleInput';
import MetricsSelector from './MetricsSelector/MetricsSelector';
import VisualizationSelector from './VisualizationSelector/VisualizationSelector';
import JourneyAnalyticsCollapsible from 'pages/Analytics/CustomAnalytics/CreateOrEditChart/JourneyAnalyticsCollapsible/JourneyAnalyticsCollapsible';
import { ReactComponent as IconRecommendation } from 'assets/icons/icon-recommendation.svg';
import {
  CHART_CONFIGURATION_TYPES,
  VISUALIZER_OPTIONS,
} from 'pages/Analytics/CustomAnalytics/CreateOrEditChart/constants';
import {
  ChartConfigState,
  ExperienceSelectorItem,
  INTERVAL_OPTIONS,
  JourneyAnalyticsCollapsibleModes,
  RecommendedMetric,
  TAnalyticsRouteParams,
} from 'pages/Analytics/types';
import {
  deserializeModuleConfig,
  generateGraphData,
  mapRecommendationToChartConfig,
  serializeChartConfig,
  shouldHideHourlyInterval,
} from 'pages/Analytics/helpers';
import experienceGraphService from 'services/api/experience-graph.api';
import useExperienceGraph from 'pages/Analytics/CustomAnalytics/hooks/useExperienceGraph';
import TemplatedExperienceAPI from 'services/api/templated-experience.api';
import { capitalizeFirstCharacter, epochToDateTimeString } from 'helpers/common.helper';
import { DATE_FORMAT_OPTIONS } from 'pages/Analytics/Overview/constants';
import { useThunkDispatch } from 'hooks/reduxHooks';
import { useQueryClient } from '@tanstack/react-query';
import LoadingIcon from 'components/Shared/LoadingIcon/LoadingIcon';
import AnalyticsErrorState from 'pages/Analytics/components/AnalyticsErrorState/AnalyticsErrorState';
import { ReactComponent as IconPushSent } from 'pages/Analytics/CustomAnalytics/icons/push-sent.svg';
import { diffObject } from 'helpers/diff-checker';
import { useFeatureFlags } from 'feature-flags';
import { EMAIL_ACTION_TYPE } from 'store/email/email.type';
import { SMS_ACTION_TYPE } from 'store/sms/sms.type';
import { ConfirmationDialogProps, ConfirmationModalTypes } from 'components/Shared/shared.types';
import { ReactComponent as IconResetMetric } from 'pages/Analytics/CustomAnalytics/icons/reset-metric.svg';
import useConfirmModal from 'hooks/useConfirmModal';

const CLASS_MAIN = 'chart-details';
const CLASS_FULL_PAGE_SPINNER = 'analytics-container__full-page-spinner';
const CLASSES = {
  FULL_PAGE_LOADER: 'analytics-container__full-page-loader',
  HEADER: `${CLASS_MAIN}__header`,
  BACK: `${CLASS_MAIN}__header__back`,
  TITLE_FIELD: `${CLASS_MAIN}__header__title-field`,
  CONTENT: `${CLASS_MAIN}__content`,
  COLUMN: `${CLASS_MAIN}__content__column`,
  FOOTER: `${CLASS_MAIN}__footer`,
};

const teAPI = new TemplatedExperienceAPI();

const INIT_CHART_CONFIG: ChartConfigState = {
  title: '',
  description: '',
  selectedExperiences: [],
  metrics: {
    title: 'Push sent',
    entity: 'push',
    metric: 'sent',
  },
  timeFrame: {
    start: null,
    end: null,
    period: INTERVAL_OPTIONS.DAILY,
    relative: [7, INTERVAL_OPTIONS.DAILY],
  },
  visualizationType: VISUALIZER_OPTIONS.LINE,
};

/**
 * Renders the label shown in the Time Frame collapsible step header.
 *
 * Branch order matters: `FlightDateRangePicker.onChange` populates BOTH `start`/`end`
 * AND the `relative` tuple when a user picks a preset (Today / Last N days), so we must
 * check `relative` first. Only the "Custom" selection leaves `relative` as
 * `[undefined, undefined]`, in which case we fall through to the absolute-date label.
 */
const renderTimeFrameSelectorLabel = (timeFrame: ChartConfigState['timeFrame']) => {
  const relativeTimeOffset = timeFrame?.relative?.[0];
  const relativeTimeUnit = timeFrame?.relative?.[1];
  const hasValidRelative =
    relativeTimeOffset !== undefined && relativeTimeOffset !== null && relativeTimeOffset >= 0 && !!relativeTimeUnit;

  if (hasValidRelative) {
    return (
      <span>
        Over the past&nbsp;
        <b>
          {relativeTimeOffset} {relativeTimeUnit}
          {(relativeTimeOffset as number) > 1 ? 's' : ''}
        </b>
        &nbsp;at a&nbsp;
        <b>{timeFrame?.period}</b>
        &nbsp;interval
      </span>
    );
  } else if (timeFrame && timeFrame.start && timeFrame.end) {
    return (
      <span>
        <b>
          {epochToDateTimeString(timeFrame.start, 'en-US', DATE_FORMAT_OPTIONS)}
          &nbsp;-&nbsp;
          {epochToDateTimeString(timeFrame.end, 'en-US', DATE_FORMAT_OPTIONS)}
        </b>
        &nbsp;at a&nbsp;
        <b>{timeFrame.period}</b>
        &nbsp;interval
      </span>
    );
  } else {
    return <></>;
  }
};

const metricResetWarningDialogProps: ConfirmationDialogProps = {
  theme: ConfirmationModalTypes.WARNING,
  icon: <IconResetMetric />,
  title: 'Metric selection will be reset',
  // TODO: Update the description to be more specific about which metric is being reset once we support dynamic metrics derivation in the chart configuration
  description: 'You are selecting an experience that might not contain the selected metric',
  secondaryAction: { value: 'Cancel' },
  primaryAction: { value: 'Select' },
  // TODO: Update the warning message to be more specific about which metric is being reset once we support dynamic metrics derivation in the chart configuration
  warningMessage: 'Pressing select will reset the metric selected for this chart.',
};

export default function CreateOrEditChart() {
  const { pid: tenantId, id: editGraphId } = useParams<TAnalyticsRouteParams>();
  const history = useHistory();
  const dispatch = useThunkDispatch();
  const queryClient = useQueryClient();
  const flags = useFeatureFlags(['tx_email_sms_custom_analytics_enabled', 'tx_custom_analytics_enhancements']);
  const isEmailSmsCustomAnalyticsConfigEnabled = flags.tx_email_sms_custom_analytics_enabled;
  const isCustomAnalyticsEnhancementsEnabled = flags.tx_custom_analytics_enhancements;

  const [isExperiencesLoading, setExperiencesLoading] = useState(false);
  const [isModuleConfigSaving, setModuleConfigSaving] = useState(false);
  // TODO: fold deserialize into the graph query / hydrate form state without hydratedGraphId.
  const [hydratedGraphId, setHydratedGraphId] = useState<string>();

  const [apiChartConfig, setApiChartConfig] = useState<ChartConfigState>(INIT_CHART_CONFIG);
  const [chartConfig, setChartConfig] = useState<ChartConfigState>(INIT_CHART_CONFIG);
  const [savedChartConfig, setSavedChartConfig] = useState<ChartConfigState>({});

  const objdiff = diffObject(apiChartConfig, chartConfig, true);
  const [numUnsavedChanges, setNumSavedChanges] = useState(
    Object.keys(objdiff.added).length + Object.keys(objdiff.updated).length + Object.keys(objdiff.removed).length,
  );

  const [chartConfigurationType, setChartConfigurationType] = useState(
    editGraphId ? CHART_CONFIGURATION_TYPES.DO_IT_YOURSELF : CHART_CONFIGURATION_TYPES.RECOMMENDED,
  );

  const [MetricResetConfirmModal, showMetricResetConfirmModal] = useConfirmModal(metricResetWarningDialogProps);

  const handleSelectChartConfigurationType = (selectedType: CHART_CONFIGURATION_TYPES) =>
    setChartConfigurationType((type) => {
      if (type === CHART_CONFIGURATION_TYPES.DO_IT_YOURSELF) {
        setChartConfig((config) => ({
          ...config,
          metrics: INIT_CHART_CONFIG.metrics,
          timeFrame: INIT_CHART_CONFIG.timeFrame,
          visualizationType: INIT_CHART_CONFIG.visualizationType,
        }));
        setSavedChartConfig((config) => ({
          ...config,
          metrics: INIT_CHART_CONFIG.metrics,
          timeFrame: INIT_CHART_CONFIG.timeFrame,
          visualizationType: INIT_CHART_CONFIG.visualizationType,
        }));

        setRecommendationSelectorCompleted(false);
        setMetricSelectorCompleted(false);
        setTimeframeSelectorCompleted(false);
        setVisualizationSelectorCompleted(false);
      }
      return selectedType;
    });

  const [selectedRecommendation, setSelectedRecommendation] = useState<RecommendedMetric>({
    icon: <IconPushSent />,
    metric: { key: 'push-sent', name: 'push notifications sent' },
    time: { period: 7, interval: 'day' },
    visualization: VISUALIZER_OPTIONS.LINE,
  });

  // Completion Status States
  const [isExperienceSelectorCompleted, setExperienceSelectorCompleted] = useState(false);
  const [isRecommendationSelectorCompleted, setRecommendationSelectorCompleted] = useState(false);
  const [isMetricSelectorCompleted, setMetricSelectorCompleted] = useState(false);
  const [isTimeframeSelectorCompleted, setTimeframeSelectorCompleted] = useState(false);
  const [isVisualizationSelectorCompleted, setVisualizationSelectorCompleted] = useState(false);

  // Validation Status States
  const [isExperienceSelectorValid, setExperienceSelectorValid] = useState(false);
  const [isMetricSelectorValid, setMetricSelectorValid] = useState(false);
  const [isTimeframeSelectorValid, setTimeframeSelectorValid] = useState(false);
  const [isVisualizationSelectorValid, setVisualizationSelectorValid] = useState(false);
  const [isChartTitleInputValid, setChartTitleInputValid] = useState(false);

  const {
    graph,
    isLoading: isModuleConfigLoading,
    isError: isModuleConfigError,
    isFetching: isModuleConfigFetching,
    refetch: refetchModuleConfig,
  } = useExperienceGraph(editGraphId ?? '');

  // See TODO on hydratedGraphId for a cleaner query/hydrate approach.
  useEffect(() => {
    if (!editGraphId) {
      if (hydratedGraphId !== undefined) {
        setHydratedGraphId(undefined);
      }
      return;
    }
    if (!graph || hydratedGraphId === graph.id) {
      return;
    }

    let cancelled = false;
    (async () => {
      const chartConfigState = await deserializeModuleConfig(graph);
      if (cancelled) return;

      setChartConfig((config) => ({ ...config, ...chartConfigState }));
      setApiChartConfig((config) => ({ ...config, ...chartConfigState }));
      setSavedChartConfig((config) => ({ ...config, ...chartConfigState }));

      // Set all steps completion
      setExperienceSelectorCompleted(true);
      setMetricSelectorCompleted(true);
      setTimeframeSelectorCompleted(true);
      setVisualizationSelectorCompleted(true);

      // Set all steps validation
      setExperienceSelectorValid(true);
      setMetricSelectorValid(true);
      setTimeframeSelectorValid(true);
      setVisualizationSelectorValid(true);
      setChartTitleInputValid(true);
      setHydratedGraphId(graph.id);
    })();

    return () => {
      cancelled = true;
    };
  }, [editGraphId, graph, hydratedGraphId]);

  const [experiencesList, setExperiencesList] = useState<ExperienceSelectorItem[]>([]);
  const fetchExperiences = useCallback(async () => {
    try {
      setExperiencesLoading(true);

      const experiences = await teAPI.getTemplatedInstances({
        limit: 9999,
        offset: 0,
      });
      setExperiencesList(
        experiences?.data
          ?.filter(({ status }) => status !== 'draft')
          .map((item) => {
            const hasEmail = item.steps?.some((step) =>
              step.actions?.some((action) => action.actionType === EMAIL_ACTION_TYPE.EMAIL),
            );
            const hasSMS = item.steps?.some((step) =>
              step.actions?.some((action) => action.actionType === SMS_ACTION_TYPE.SMS),
            );
            return {
              key: item.id || '',
              name: item.name || item.id || '',
              hasEmail,
              hasSMS,
            };
          }),
      );
    } catch {
      dispatch({
        type: 'SHOW_SNACKBAR',
        payload: { content: 'Something went wrong while fetching experience selector list', type: 'error' },
      });
    } finally {
      setExperiencesLoading(false);
    }
  }, [dispatch]);
  useEffect(() => {
    fetchExperiences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply Handlers - Completion Status
  const handleApplyRecommendationSelector = () => {
    setRecommendationSelectorCompleted(true);
    setChartConfig((config) => ({
      ...mapRecommendationToChartConfig(selectedRecommendation, config),
    }));
    setSavedChartConfig((config) => ({
      ...mapRecommendationToChartConfig(selectedRecommendation, config),
    }));

    // Apply the `metrics`, `timeFrame`, and `visualization` steps in DIY and mark them as valid
    setMetricSelectorCompleted(true);
    setMetricSelectorValid(true);
    setTimeframeSelectorCompleted(true);
    setTimeframeSelectorValid(true);
    setVisualizationSelectorCompleted(true);
    setVisualizationSelectorValid(true);
  };
  const handleApplyExperienceSelector = () => {
    setExperienceSelectorCompleted(true);
    setSavedChartConfig((config) => ({
      ...config,
      selectedExperiences: chartConfig.selectedExperiences,
      description: chartConfig.description,
    }));
  };
  const handleApplyExperienceSelectorEnhanced = async () => {
    if (chartConfig.metrics?.metric && chartConfig.selectedExperiences?.[0]?.key) {
      if (await showMetricResetConfirmModal()) {
        setExperienceSelectorCompleted(true);
        setSavedChartConfig((config) => ({
          ...config,
          selectedExperiences: chartConfig.selectedExperiences,
          description: chartConfig.description,
        }));
      } else {
        setChartConfig((config) => ({
          ...config,
          selectedExperiences: savedChartConfig.selectedExperiences,
          description: savedChartConfig.description,
        }));
      }
    }
  };
  const handleApplyMetricSelector = () => {
    setMetricSelectorCompleted(true);
    setSavedChartConfig((config) => ({
      ...config,
      metrics: chartConfig.metrics,
    }));
  };
  const handleApplyTimeframeSelector = () => {
    setTimeframeSelectorCompleted(true);
    setSavedChartConfig((config) => ({
      ...config,
      timeFrame: chartConfig.timeFrame,
    }));
  };
  const handleApplyVisualizationSelector = () => {
    setVisualizationSelectorCompleted(true);
    setSavedChartConfig((config) => ({
      ...config,
      visualizationType: chartConfig.visualizationType,
    }));
  };

  // Change Handlers
  const handleChangeRecommendationSelector = useCallback(
    (recommendation: RecommendedMetric) => setSelectedRecommendation(recommendation),
    [],
  );
  const handleChangeExperienceSelector = (experiences: ExperienceSelectorItem[]) =>
    setChartConfig((config) => ({
      ...config,
      selectedExperiences: experiences,
      description: `from ${experiences[0].name}`,
    }));
  const handleChangeMetricSelector = (metrics: ChartConfigState['metrics']) =>
    setChartConfig((config) => ({ ...config, metrics }));
  const handleChangeTimeFrameSelector = (timeFrame: ChartConfigState['timeFrame']) =>
    setChartConfig((config) => ({ ...config, timeFrame }));
  const handleChangeVisualizationSelector = (visualizationType: ChartConfigState['visualizationType']) =>
    setChartConfig((config) => ({ ...config, visualizationType }));
  const handleChangeChartTitleInput = (chartTitle: ChartConfigState['title']) =>
    setChartConfig((config) => ({ ...config, title: chartTitle }));

  // Validation Handlers - Validation Status
  const handleExperienceSelectorValidation = useCallback((isValid: boolean) => setExperienceSelectorValid(isValid), []);
  const handleMetricSelectorValidation = useCallback((isValid: boolean) => setMetricSelectorValid(isValid), []);
  const handleTimeframeSelectorValidation = useCallback((isValid: boolean) => setTimeframeSelectorValid(isValid), []);
  const handleVisualizationSelectorValidation = useCallback(
    (isValid: boolean) => setVisualizationSelectorValid(isValid),
    [],
  );
  const handleChartTitleInputValidation = useCallback((isValid: boolean) => setChartTitleInputValid(isValid), []);

  /** Title lives in chartConfig while editing; savedChartConfig.title is not updated per keystroke (avoids extra renders). */
  const isChartTitleStepComplete = useMemo(
    () => isChartTitleInputValid && !!chartConfig.title?.trim(),
    [isChartTitleInputValid, chartConfig.title],
  );

  const previewData = useMemo(() => generateGraphData(chartConfig.visualizationType), [chartConfig.visualizationType]);

  const renderExperienceStep = (totalSteps: number) => (
    <JourneyAnalyticsCollapsible
      key="experience"
      step={1}
      totalSteps={totalSteps}
      defaultCollapsed={false}
      mode={
        !isExperienceSelectorCompleted
          ? JourneyAnalyticsCollapsibleModes.DRAFT
          : JourneyAnalyticsCollapsibleModes.COMPLETED
      }
      title={'Select an experience'}
      label={
        <span>
          Based on&nbsp;<b>{savedChartConfig.selectedExperiences?.[0]?.name || ''}</b>
        </span>
      }
      actionLabel={'Apply'}
      actionHandler={() =>
        isCustomAnalyticsEnhancementsEnabled ? handleApplyExperienceSelectorEnhanced() : handleApplyExperienceSelector()
      }
      secondaryActionHandler={() =>
        setChartConfig((config) => ({
          ...config,
          selectedExperiences: savedChartConfig.selectedExperiences,
          description: savedChartConfig.description,
        }))
      }
      isValid={isExperienceSelectorValid}
    >
      <ExperienceSelector
        chartConfig={chartConfig}
        experiences={experiencesList}
        onChange={handleChangeExperienceSelector}
        onValidation={handleExperienceSelectorValidation}
        isLoading={isExperiencesLoading}
      />
    </JourneyAnalyticsCollapsible>
  );

  const renderStepsForRecommendation = () => (
    <>
      {renderExperienceStep(2)}
      {/* Select a recommendation */}
      <JourneyAnalyticsCollapsible
        key={2}
        step={2}
        totalSteps={2}
        mode={
          !isRecommendationSelectorCompleted
            ? JourneyAnalyticsCollapsibleModes.DRAFT
            : JourneyAnalyticsCollapsibleModes.COMPLETED
        }
        title={'Select a recommendation'}
        titleIcon={<IconRecommendation />}
        description={'Make chart building easy by selecting a pre-defined option'}
        label={
          selectedRecommendation ? (
            <span>
              For the selected experience, show me&nbsp;
              <b>{selectedRecommendation.metric.name}</b>
              &nbsp;in the&nbsp;
              <b>
                past {selectedRecommendation.time.period} {selectedRecommendation.time.interval}s
              </b>
            </span>
          ) : (
            <></>
          )
        }
        actionLabel={'Apply'}
        actionHandler={handleApplyRecommendationSelector}
        // RecommendationSelector always has the first RECOMMENDED_METRICS option selected by default
        isValid={true}
      >
        <RecommendationsSelector
          chartConfig={chartConfig}
          isEmailSmsOptionEnabled={isEmailSmsCustomAnalyticsConfigEnabled}
          onChange={handleChangeRecommendationSelector}
        />
      </JourneyAnalyticsCollapsible>
    </>
  );

  const renderStepsForDIY = () => (
    <>
      {renderExperienceStep(4)}
      {/* Select metrics to display (vertical) */}
      <JourneyAnalyticsCollapsible
        key="diy-metrics-step"
        step={2}
        totalSteps={4}
        mode={
          !isMetricSelectorCompleted
            ? JourneyAnalyticsCollapsibleModes.DRAFT
            : JourneyAnalyticsCollapsibleModes.COMPLETED
        }
        title={'Select metrics to display (vertical)'}
        label={
          <span>
            I want to measure&nbsp;
            <b>{`${capitalizeFirstCharacter(savedChartConfig.metrics?.entity || '')} ${capitalizeFirstCharacter(
              savedChartConfig.metrics?.metric || '',
            )}`}</b>
          </span>
        }
        actionLabel={'Apply'}
        actionHandler={handleApplyMetricSelector}
        secondaryActionHandler={() => setChartConfig((config) => ({ ...config, metrics: savedChartConfig.metrics }))}
        isValid={isMetricSelectorValid}
      >
        <MetricsSelector
          chartConfig={chartConfig}
          isEmailSmsOptionEnabled={isEmailSmsCustomAnalyticsConfigEnabled}
          onChange={handleChangeMetricSelector}
          onValidation={handleMetricSelectorValidation}
        />
      </JourneyAnalyticsCollapsible>
      {/* Select a time frame (horizontal) */}
      <JourneyAnalyticsCollapsible
        key={3}
        step={3}
        totalSteps={4}
        mode={
          !isTimeframeSelectorCompleted
            ? JourneyAnalyticsCollapsibleModes.DRAFT
            : JourneyAnalyticsCollapsibleModes.COMPLETED
        }
        title={'Select a time frame (horizontal)'}
        label={renderTimeFrameSelectorLabel(savedChartConfig.timeFrame)}
        actionLabel={'Apply'}
        secondaryActionHandler={() =>
          setChartConfig((config) => ({ ...config, timeFrame: savedChartConfig.timeFrame }))
        }
        actionHandler={handleApplyTimeframeSelector}
        isValid={isTimeframeSelectorValid}
      >
        <TimeFrameSelector
          hiddenIntervals={shouldHideHourlyInterval(chartConfig.timeFrame) ? [INTERVAL_OPTIONS.HOURLY] : []}
          chartConfig={chartConfig}
          onChange={handleChangeTimeFrameSelector}
          onValidation={handleTimeframeSelectorValidation}
        />
      </JourneyAnalyticsCollapsible>
      {/* Select visualization */}
      <JourneyAnalyticsCollapsible
        key={4}
        step={4}
        totalSteps={4}
        mode={
          !isVisualizationSelectorCompleted
            ? JourneyAnalyticsCollapsibleModes.DRAFT
            : JourneyAnalyticsCollapsibleModes.COMPLETED
        }
        title={'Select visualization'}
        label={
          <span>
            In a&nbsp;
            <b>
              {savedChartConfig.visualizationType} {`${savedChartConfig.visualizationType !== 'table' ? 'graph' : ''}`}
            </b>
          </span>
        }
        actionLabel={'Preview chart'}
        actionHandler={handleApplyVisualizationSelector}
        secondaryActionHandler={() =>
          setChartConfig((config) => ({ ...config, visualizationType: savedChartConfig.visualizationType }))
        }
        isValid={isVisualizationSelectorValid}
      >
        <VisualizationSelector
          chartConfig={chartConfig}
          onChange={handleChangeVisualizationSelector}
          onValidation={handleVisualizationSelectorValidation}
        />
      </JourneyAnalyticsCollapsible>
    </>
  );

  const isSaveButtonEnabled =
    chartConfigurationType === CHART_CONFIGURATION_TYPES.RECOMMENDED
      ? isExperienceSelectorValid &&
        isExperienceSelectorCompleted &&
        isRecommendationSelectorCompleted &&
        isChartTitleInputValid &&
        isChartTitleStepComplete
      : isExperienceSelectorValid &&
        isExperienceSelectorCompleted &&
        isMetricSelectorValid &&
        isMetricSelectorCompleted &&
        isTimeframeSelectorValid &&
        isTimeframeSelectorCompleted &&
        isVisualizationSelectorValid &&
        isVisualizationSelectorCompleted &&
        isChartTitleInputValid &&
        isChartTitleStepComplete;

  // Create/Save custom analytics chart config
  const handleSaveToDashboard = async () => {
    let graphId = editGraphId || '';

    // Persist Applied section state only (`savedChartConfig` is updated on each step's Apply).
    // `chartConfig` is the live draft and may include un-Applied edits (Cancel restores from saved).
    // Title is the exception: it is edited outside the Apply flow and lives on `chartConfig`.
    const trimmedTitle = chartConfig.title?.trim() || '';
    const moduleConfigPayload = serializeChartConfig({ ...savedChartConfig, title: trimmedTitle }, graphId, tenantId);

    try {
      setModuleConfigSaving(true);

      if (graphId) {
        await experienceGraphService.updateExperienceGraph(moduleConfigPayload);
      } else {
        const { id } = await experienceGraphService.createExperienceGraph(moduleConfigPayload);
        graphId = id;
      }

      setNumSavedChanges(0);
      queryClient.invalidateQueries(['experience-graphs']);
      queryClient.invalidateQueries(['experience-graph', { id: graphId }]);
      history.replace(`/project/${tenantId}/analytics?recentGraphId=${graphId}&showCustomAnalyticsTab`);
      dispatch({
        type: 'SHOW_SNACKBAR',
        payload: {
          content: `The custom analytics graph config was ${graphId ? 'saved' : 'created'} successfully`,
          type: 'success',
        },
      });
    } catch {
      dispatch({
        type: 'SHOW_SNACKBAR',
        payload: { content: 'Something went wrong while saving the custom analytics graph config', type: 'error' },
      });
    } finally {
      setModuleConfigSaving(false);
    }
  };

  if (editGraphId && isModuleConfigLoading) {
    return (
      <main className={CLASS_FULL_PAGE_SPINNER}>
        <LoadingIcon />
      </main>
    );
  }

  if (editGraphId && isModuleConfigError) {
    return (
      <main className={CLASS_MAIN}>
        <header className={CLASSES.HEADER}>
          <FlightButton
            ariaLabel={'Go Back'}
            className={CLASSES.BACK}
            iconLeft={'baselineKeyboardArrowLeft'}
            theme="link"
            onClick={() => history.push(`/project/${tenantId}/analytics?showCustomAnalyticsTab`)}
          />
        </header>
        <AnalyticsErrorState onRetry={() => refetchModuleConfig()} isRetrying={isModuleConfigFetching} />
      </main>
    );
  }

  return (
    <main className={CLASS_MAIN}>
      {isModuleConfigSaving && <div className={CLASSES.FULL_PAGE_LOADER} />}
      <header className={CLASSES.HEADER}>
        <FlightButton
          ariaLabel={'Go Back'}
          className={CLASSES.BACK}
          iconLeft={'baselineKeyboardArrowLeft'}
          theme="link"
          onClick={() => history.push(`/project/${tenantId}/analytics?showCustomAnalyticsTab`)}
        />
        <div className={CLASSES.TITLE_FIELD}>
          <ChartTitleInput
            chartConfig={chartConfig}
            onChange={handleChangeChartTitleInput}
            onValidation={handleChartTitleInputValidation}
            placeholderText="Title your chart"
            variant="pageHeader"
          />
        </div>
      </header>
      <div className={CLASSES.CONTENT}>
        <div className={CLASSES.COLUMN}>
          {!editGraphId && (
            <ChartConfigurationType
              currrentSelection={chartConfigurationType}
              onSelect={handleSelectChartConfigurationType}
            />
          )}
          {chartConfigurationType === CHART_CONFIGURATION_TYPES.RECOMMENDED
            ? renderStepsForRecommendation()
            : renderStepsForDIY()}
        </div>
        <div className={CLASSES.COLUMN}>
          <Preview chartConfig={chartConfig} data={previewData} />
        </div>
      </div>
      <footer className={CLASSES.FOOTER}>
        <FlightButton
          theme="secondary"
          onClick={() => history.push(`/project/${tenantId}/analytics?showCustomAnalyticsTab`)}
          label={'Cancel'}
        />
        <FlightButton
          theme="primary"
          onClick={handleSaveToDashboard}
          label={'Save to dashboard'}
          disabled={!isSaveButtonEnabled || (!!editGraphId && hydratedGraphId !== editGraphId)}
          loading={isModuleConfigSaving}
        />
      </footer>
      {isCustomAnalyticsEnhancementsEnabled && <MetricResetConfirmModal />}
      <UnsavedPrompt
        when={numUnsavedChanges > 0}
        dialogProps={{
          description: 'This will undo all the information you have entered since you started.',
        }}
      />
    </main>
  );
}
